/**
 * Google tag (gtag.js) for Google Analytics and Google Ads.
 * Accepts Google Analytics (G-…), Google tag (GT-…) and Google Ads (AW-…) IDs.
 */
// GT-NNQSDSCB is left out: if that Google tag also sends to G-3NKQJV9ZYR, every page view would be counted twice.
export const GOOGLE_TAG_IDS: string[] = ['G-3NKQJV9ZYR', 'AW-11342224443']

/**
 * Optional Google Ads conversion for demo requests, in the form "AW-XXXXXXXXXX/label".
 * Find it in Google Ads → Goals → Conversions → your conversion → Tag setup.
 */
export const DEMO_CONVERSION_SEND_TO = ''

type GtagArgs = [string, ...unknown[]]

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: GtagArgs) => void
  }
}

const TAG_ID_RE = /^(G|GT|AW)-[A-Z0-9]+$/
const validIds = GOOGLE_TAG_IDS.filter((id) => TAG_ID_RE.test(id))

let initialised = false

function isEnabled(): boolean {
  return typeof window !== 'undefined' && validIds.length > 0
}

export function initAnalytics(): void {
  if (initialised || !isEnabled()) return
  initialised = true

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${validIds[0]}`
  document.head.appendChild(script)

  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag(...args: GtagArgs) {
    // gtag expects the raw arguments object pushed onto the data layer
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments)
  }
  window.gtag('js', new Date())
  // Page views are sent manually on every route change
  validIds.forEach((id) => window.gtag?.('config', id, { send_page_view: false }))
}

export function trackPageView(path: string): void {
  if (!isEnabled() || !window.gtag) return
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  })
}

export function trackEvent(name: string, params: Record<string, string | number> = {}): void {
  if (!isEnabled() || !window.gtag) return
  window.gtag('event', name, params)
}

/** Records a successful demo request as a lead, plus a Google Ads conversion when configured. */
export function trackDemoRequest(businessType: string): void {
  trackEvent('generate_lead', { form: 'request_demo', business_type: businessType })
  if (DEMO_CONVERSION_SEND_TO && window.gtag) {
    window.gtag('event', 'conversion', { send_to: DEMO_CONVERSION_SEND_TO })
  }
}
